import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelectionList } from '@angular/material/list'
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { RentalReimbursementInfoComponent } from './rental-reimbursement-info/rental-reimbursement-info.component';
import { RentalAndTowCustomerComponent } from '../rental-and-tow-customer/rental-and-tow-customer.component';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { ClaimService } from '../../../app/claims/claim.service';
import { LoadingService } from '../../../shared/loading.service';
import { ReimbursementRequest } from '../reimbursement-request';
import { TowReimbursementInfoComponent } from './tow-reimbursement-info/tow-reimbursement-info.component';
import { ReimbursementConstants } from '../../../shared/constants/reimbursement-constants';
import { KeyValuePair } from '../../../shared/key-value-pair';
import { Subscription } from 'rxjs';
import { ReimbursementFormMapper } from '../reimbursement-mapper';
import { TripReimbursementInfoComponent } from './trip-reimbursement-info/trip-reimbursement-info.component';

@Component({
  selector: 'rental-and-tow',
  templateUrl: './rental-and-tow.component.html'
})
export class RentalAndTowComponent implements OnInit {

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('checkboxList') reimbursementTypeCheckbox: MatCheckbox;
  @ViewChild('customer') customer: RentalAndTowCustomerComponent;
  @ViewChild('rentalInvoice') rentalInvoice: RentalReimbursementInfoComponent;
  @ViewChild('towInvoice') towInvoice: TowReimbursementInfoComponent;
  @ViewChild('tripInvoice') tripInvoice: TripReimbursementInfoComponent;
  @ViewChild('reimbursementTypesSelected') reimbursementTypesSelected: MatSelectionList;

  public reimbursementTypeForm: UntypedFormGroup;
  public formCompleted = false;
  public missingFieldsError = false;
  public submitClicked = false;
  public claimServiceSubscription: Subscription;
  public reimbursementData: ReimbursementRequest = null;
  public modalMissingFileRef: MatDialogRef<DialogComponent>;
  public reimbursementTypes: KeyValuePair[] = ReimbursementConstants.ReimbursementTypes;
  public showRentalInvoice = true;
  public showTowInvoice = true;
  public showTripInvoice = true;
  public windowSize: number;

  constructor(private claimService: ClaimService, private router: Router, private loadingService: LoadingService, private dialog: MatDialog) { }

  public get title() {
    return (this.stepper?.selectedIndex === 1) ? "Invoice Information" : "Customer Information";
  }

  ngOnInit(): void {
    this.reimbursementTypeForm = new UntypedFormGroup({
      'reimbursementType': new UntypedFormControl(false, [atLeastOneSelected()])
    });
  }

  public backClicked(): void {
    this.stepper.previous();
    this.missingFieldsError = this.stepper.selected.state === 'INVALID';
  }

  public nextClicked(): void {

    if (this.stepper.selectedIndex === 0) {
      this.customer.validateForm();
      if (this.reimbursementTypesSelected.selectedOptions?.selected.length === 0) {
        this.reimbursementTypeForm.get('reimbursementType').setErrors({ noneSelected: true });
      }
      this.stepper.selected.completed = (this.reimbursementTypesSelected.selectedOptions?.selected?.length > 0 && this.customer.formCompleted);
      this.missingFieldsError = !this.stepper.selected.completed;
      this.showRentalInvoice = (this.isTypeSelected(ReimbursementConstants.RENTAL));
      this.showTowInvoice = (this.isTypeSelected(ReimbursementConstants.TOWING));
      this.showTripInvoice = (this.isTypeSelected(ReimbursementConstants.TRIP));
    }
    if (this.stepper.selected.completed) {
      this.stepper.next();
    }
  }

  ngOnDestroy(): void {
    if (this.claimServiceSubscription) {
      this.claimServiceSubscription.unsubscribe();
    }
  }

  public validateInvoicesStep(): void {
    this.stepper.selected.completed = false;

    if (this.showRentalInvoice) {
      this.rentalInvoice.validateForm();
    }
    if (this.showTowInvoice) {
      this.towInvoice.validateForm();
    }
    if (this.showTripInvoice) {
      this.tripInvoice.validateForm();
    }
  }

  public isTypeSelected(value: string): boolean {
    return this.reimbursementTypesSelected.selectedOptions?.selected?.findIndex(x => x.value === value) >= 0
  }

  public submit(): void {
    this.validateInvoicesStep();

    if (this.validateAllRequiredData()) {
      this.loadingService.show();
      const rentalInvoiceFile: File[] = this.rentalInvoice?.importFiles ?? [];
      const towInvoiceFile: File = this.towInvoice?.importFiles[0] ?? null;
      const tripInvoiceFile: File[] = this.tripInvoice?.importFiles ?? [];
      this.saveReimbursementClaim(rentalInvoiceFile, towInvoiceFile, tripInvoiceFile);
    }
  }

  private validateAllRequiredData(): boolean {
    return (this.customer.formCompleted
            && (!this.showRentalInvoice || this.rentalInvoice?.fileUpload.formCompleted)
            && (!this.showTowInvoice || this.towInvoice?.fileUpload.formCompleted)
            && (!this.showTripInvoice || this.tripInvoice?.fileUpload.formCompleted)
    );
  }

  private saveReimbursementClaim(rentalInvoiceFile: File[], towInvoiceFile: File, tripInvoiceFile: File[]) {
    this.submitClicked = true;
    if (rentalInvoiceFile?.length > 0) { this.consolidateFormData(ReimbursementConstants.RENTAL, rentalInvoiceFile); }
    if (towInvoiceFile) { this.consolidateFormData(ReimbursementConstants.TOWING, [towInvoiceFile]); }
    if (tripInvoiceFile?.length > 0) { this.consolidateFormData(ReimbursementConstants.TRIP, tripInvoiceFile); }
  }

  public submitEachInvoice(invoiceFiles: File[]) {
    this.claimServiceSubscription = this.claimService.saveReimbursementClaim(invoiceFiles, this.reimbursementData)
      .subscribe({
        next: _ => {
          this.loadingService.hide();
          this.router.navigateByUrl("/reimbursement/claim-submitted");
        },
        error: error => {
          this.loadingService.hide();
          alert('Error submitting ');
          this.submitClicked = false;
          throw (error);
        }
      });
  }

  public consolidateFormData(reimbursementType: string, invoiceFiles: File[]): void {
    const newForm = new UntypedFormGroup({});
    this.addControls(newForm, this.customer.rentalAndTowCustomerForm);
    this.reimbursementData = new ReimbursementRequest();
    this.reimbursementData = ReimbursementFormMapper.mapClaimRequest(newForm);
    this.reimbursementData.reimbursementType = reimbursementType;
    this.submitEachInvoice(invoiceFiles);
  }

  public addControls(finalForm: UntypedFormGroup, partialForm: UntypedFormGroup): void {
    Object.keys(partialForm.controls).forEach((key: string) =>
      finalForm.addControl(key, partialForm.controls[key])
    );
  }
}

export function atLeastOneSelected(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    return (value === undefined || value === null) ? { noneSelected: true } : null;
  }
}